import { metaEventsService } from '../services/metaEvents';
import {
  createMetaEventId,
  getMetaCookies,
  getMetaEventSourceUrl,
  hasStoredMarketingConsent,
} from './metaTracking';

type MetaEventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

interface FbqFunction {
  (...args: unknown[]): void;
  callMethod?(...args: unknown[]): void;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: FbqFunction;
}

const PRO_PLAN_VALUE_EUR: Record<'monthly' | 'yearly', number> = {
  monthly: 18,
  yearly: 180,
};

let marketingConsentGranted = false;
let pixelInitialized = false;

const queuedEvents: Array<{ event: string; params?: MetaEventParams; eventId: string }> = [];

export const setMetaMarketingConsent = (granted: boolean): void => {
  marketingConsentGranted = granted;
  if (!granted) {
    return;
  }
  loadMetaPixel();
};

export const isMetaPixelConfigured = (): boolean =>
  typeof window !== 'undefined' &&
  window.location.hostname === 'leadsme.app' &&
  !!process.env.REACT_APP_META_PIXEL_ID;

const canTrack = (): boolean => isMetaPixelConfigured() && marketingConsentGranted;

const flushMetaQueue = (): void => {
  if (!window.fbq || !pixelInitialized) return;

  queuedEvents.splice(0).forEach(({ event, params, eventId }) => {
    trackMetaEventInternal(event, params, eventId);
  });
};

export const loadMetaPixel = (): void => {
  const pixelId = process.env.REACT_APP_META_PIXEL_ID;
  if (!pixelId || !canTrack() || pixelInitialized) return;

  if (window.fbq) {
    pixelInitialized = true;
    flushMetaQueue();
    return;
  }

  if (document.querySelector('script[data-meta-pixel="leadsme"]')) {
    return;
  }

  const fbq: FbqFunction = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  };

  if (!window._fbq) window._fbq = fbq;
  window.fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.setAttribute('data-meta-pixel', 'leadsme');
  script.onload = () => {
    window.fbq?.('init', pixelId);
    window.fbq?.('track', 'PageView');
    pixelInitialized = true;
    flushMetaQueue();
  };
  document.head.appendChild(script);
};

function trackMetaEventInternal(event: string, params: MetaEventParams | undefined, eventId: string): void {
  if (!window.fbq || !pixelInitialized) return;

  if (params) {
    window.fbq('track', event, params, { eventID: eventId });
  } else {
    window.fbq('track', event, {}, { eventID: eventId });
  }
}

export const trackMetaEvent = (
  event: string,
  params?: MetaEventParams,
  eventId: string = createMetaEventId(),
): string => {
  if (!canTrack()) return eventId;

  try {
    if (window.fbq && pixelInitialized) {
      trackMetaEventInternal(event, params, eventId);
    } else {
      queuedEvents.push({ event, params, eventId });
      loadMetaPixel();
    }
  } catch {
    // ignore analytics errors
  }

  return eventId;
};

export const trackMetaPageView = (): void => {
  trackMetaEvent('PageView');
};

export const trackMetaCompleteRegistration = (method: 'email' | 'google'): void => {
  const eventId = trackMetaEvent('CompleteRegistration', { status: true, method });

  if (!hasStoredMarketingConsent()) {
    return;
  }

  const { fbp, fbc } = getMetaCookies();
  void metaEventsService.sendEvent({
    eventName: 'CompleteRegistration',
    eventId,
    eventSourceUrl: getMetaEventSourceUrl(),
    fbp,
    fbc,
    marketingConsent: true,
    customData: { status: true, method },
  });
};

export const trackMetaInitiateCheckout = (type: 'monthly' | 'yearly'): void => {
  const params = {
    value: PRO_PLAN_VALUE_EUR[type],
    currency: 'EUR',
    content_name: type === 'monthly' ? 'pro_monthly' : 'pro_yearly',
  };
  const eventId = trackMetaEvent('InitiateCheckout', params);

  if (!hasStoredMarketingConsent()) {
    return;
  }

  const { fbp, fbc } = getMetaCookies();
  void metaEventsService.sendEvent({
    eventName: 'InitiateCheckout',
    eventId,
    eventSourceUrl: getMetaEventSourceUrl(),
    fbp,
    fbc,
    marketingConsent: true,
    customData: params,
  });
};

export const trackMetaPurchase = (
  type: 'monthly' | 'yearly',
  eventId: string = createMetaEventId(),
): string => {
  const params = {
    value: PRO_PLAN_VALUE_EUR[type],
    currency: 'EUR',
    content_name: type === 'monthly' ? 'pro_monthly' : 'pro_yearly',
  };
  trackMetaEvent('Purchase', params, eventId);
  return eventId;
};
